import { doc, Firestore, getDoc } from "firebase/firestore";

const KIOSK_DEVICE_ID_STORAGE_KEY = "mytime-kiosk-device-id";

type ClockInAccess =
    | {
        allowed: true;
        source: "kiosk" | "remote";
        deviceId: string;
        deviceLocation?: string;
    }
    | {
        allowed: false;
        reason: "device_not_approved" | "settings_unavailable";
    };

export const getKioskDeviceId = (): string => {
    return localStorage.getItem(KIOSK_DEVICE_ID_STORAGE_KEY) ?? "";
};

export const generateKioskDeviceId = (): string => {
    const deviceId = crypto.randomUUID();
    localStorage.setItem(KIOSK_DEVICE_ID_STORAGE_KEY, deviceId);
    return deviceId;
};

export const getClockInAccess = async (
    db: Firestore,
    remoteWorkApproved: boolean
): Promise<ClockInAccess> => {
    const deviceId = getKioskDeviceId();

    if (deviceId) {
        try {
            const settingsSnapshot = await getDoc(doc(db, "settings", "timeClock"));
            const settings = settingsSnapshot.data();
            const kiosks = settings?.kiosks;

            if (kiosks && typeof kiosks === "object" && !Array.isArray(kiosks)) {
                for (const kiosk of Object.values(kiosks)) {
                    if (!kiosk || typeof kiosk !== "object" || Array.isArray(kiosk)) continue;

                    const kioskData = kiosk as Record<string, unknown>;
                    const allowedDeviceIds = kioskData.allowedDeviceIds;
                    const deviceLocation = kioskData.location;
                    const deviceIsAllowed = Array.isArray(allowedDeviceIds)
                        && allowedDeviceIds.some((allowedDeviceId) =>
                            typeof allowedDeviceId === "string"
                            && allowedDeviceId.trim() === deviceId
                        );

                    if (deviceIsAllowed) {
                        return {
                            allowed: true,
                            source: "kiosk",
                            deviceId,
                            ...(typeof deviceLocation === "string" && deviceLocation.trim()
                                ? { deviceLocation: deviceLocation.trim() }
                                : {}),
                        };
                    }
                }
            }

            const allowedDeviceIds = settings?.allowedKioskDevices
                ?? settings?.allowedKioskDeviceIds;

            if (
                Array.isArray(allowedDeviceIds) &&
                allowedDeviceIds.some((allowedDeviceId) =>
                    typeof allowedDeviceId === "string"
                    && allowedDeviceId.trim() === deviceId
                )
            ) {
                return { allowed: true, source: "kiosk", deviceId };
            }
        } catch (error) {
            console.error("Unable to read kiosk settings:", error);
            if (!remoteWorkApproved) {
                return { allowed: false, reason: "settings_unavailable" };
            }
        }
    }

    if (remoteWorkApproved) {
        return { allowed: true, source: "remote", deviceId };
    }

    return { allowed: false, reason: "device_not_approved" };
};
